import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Grid,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Toolbar,
  AppBar,
} from "@mui/material";
import "react-toastify/dist/ReactToastify.css";
import TrainingsPlanIcon from "../../assets/TrainingsPlanIcon.png";
import CssBaseline from "@mui/material/CssBaseline";
import { useTranslation } from "react-i18next";
import DynamicDeskLogo from "../../assets/DD_2_Weiss.png";
import { useAuthStore } from "../../stores/zustand";
import billingApiService from "../../services/api/BillingApiService";
import { authApi, settingsApi } from "../../services";
import { useQueryClient } from "@tanstack/react-query";
import UserAccountPopover from "../../components/layout/UserAccountPopover";
import AccountCircle from "@mui/icons-material/AccountCircle";

const Checkout = () => {
  const { t, i18n } = useTranslation();
  const { user, currentProduct, clearAuth, preferredLanguage } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Loading and dialogue states
  const [isLoading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [redirectHome, setRedirectHome] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Listen for language changes from Electron menu
  useEffect(() => {
    if (window.electronAPI?.language) {
      const cleanup = window.electronAPI.language.onChange((newLanguage) => {
        i18n.changeLanguage(newLanguage);
      });

      return cleanup; // Cleanup on unmount
    }
  }, [i18n]);

  const hasCourseAccess = currentProduct === "preventive_course";
  const productKey = hasCourseAccess ? "preventive_course" : currentProduct;

  const openError = (message, allowedToRedirect = false) => {
    setErrorMessage(message);
    setRedirectHome(allowedToRedirect);
    setErrorDialogOpen(true);
  };

  const handleDialogClose = () => {
    setErrorDialogOpen(false);
    if (redirectHome) {
      if (currentProduct === "physio_therapist")
        navigate("/physio-video-selection");
      else navigate("/home");
    }
  };

  // Toggle between English and German language
  // Syncs with AuthStore, i18n, backend, and Electron if available
  const handleLanguageToggle = useCallback(async () => {
    const currentLanguage = useAuthStore.getState().preferredLanguage;
    const newLanguage = currentLanguage === "en" ? "de" : "en";

    // Update AuthStore language preference
    useAuthStore.getState().setPreferredLanguage(newLanguage);

    // Sync to i18n (for translations only)
    i18n.changeLanguage(newLanguage);

    // Update backend
    try {
      await settingsApi.updateLanguage(newLanguage);
    } catch (error) {
      console.error(
        "[MainContainer] Failed to update language in backend:",
        error
      );
    }

    // Notify Electron if present
    if (window.electronAPI?.language) {
      window.electronAPI.language.notifyChange(newLanguage);
    }
  }, [i18n]);

  // Logout: clear caches, auth state, and navigate to login
  // Properly cleans up TanStack Query cache and Zustand auth store
  const handleLogout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error("Logout API call failed:", error);
    }

    // Clear TanStack Query cache
    queryClient.clear();

    // Clear AuthStore
    clearAuth();

    if (window.electronAPI?.navigation) {
      try {
        await window.electronAPI.navigation.navigateToAuthenticatedState(false);
      } catch (navError) {
        console.warn("[MainContainer] Electron navigation failed:", navError);
        navigate("/");
      }
    } else {
      navigate("/");
    }
  }, [queryClient, clearAuth, navigate]);

  // Account popover handlers for user menu
  const handleAccountClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleAccountClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const onBuy = async () => {
    if (!productKey) {
      openError();
      return;
    }
    setLoading(true);
    try {
      const result = await billingApiService.startCheckout(productKey);
      if (result?.url) {
        window.location.assign(result.url);
        return;
      }
      if (result?.portalUrl) {
        window.location.assign(result.portalUrl);
        return;
      }
      openError();
    } catch (e) {
      if (e?.response?.status === 409)
        openError(t("checkoutErrorProductAlreadyPurchased"), true);
      else openError();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="lg" sx={{ height: "100vh" }}>
      <CssBaseline />
      <AppBar position="fixed">
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <img
            src={DynamicDeskLogo}
            style={{ width: "9.5%", height: "9.5%", paddingTop: "12px" }}
            alt="DynamicDeskLogo"
          />
          <IconButton
            size="large"
            aria-label="account menu"
            color="inherit"
            onClick={handleAccountClick}
          >
            {" "}
            <AccountCircle style={{ fontSize: "32px" }} />
          </IconButton>
        </Toolbar>
      </AppBar>

      <UserAccountPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleAccountClose}
        onLanguageToggle={handleLanguageToggle}
        onLogout={handleLogout}
        currentLanguage={preferredLanguage}
        userEmail={user.email}
        isCheckoutPage={true}
        // isElectron={isElectron}
      />
      <Grid container sx={{ height: "100%" }}>
        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            p: 8,
          }}
        >
          <Typography variant="h3" gutterBottom sx={{ color: "#fff" }}>
            <b>{t("unlockFullPotential")}</b>
          </Typography>
          <Typography variant="h6" sx={{ color: "#fff", marginTop: "36px" }}>
            <b>{t("goPremiumSubtitle")}</b>
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 1,
          }}
        >
          <div
            style={{ width: "100vh", padding: "36px" }}
            className="white-background"
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                width: "100%",
              }}
            >
              <img
                src={TrainingsPlanIcon}
                style={{
                  width: "124px",
                  height: "124px",
                  alignSelf: "flex-end",
                  marginBottom: "36px",
                }}
                alt="TrainingsPlanIcon"
              />
              <Typography
                component="h1"
                variant="h5"
                style={{ color: "#063bd0" }}
              >
                <b>{t("subscribe")}</b>
              </Typography>
              <Typography
                component="h1"
                variant="h6"
                style={{ color: "#063bd0", marginTop: "8px" }}
              >
                <b>
                  {hasCourseAccess
                    ? t("forFullAccessPreventive")
                    : t("forFullAccess")}{" "}
                </b>
              </Typography>
              <p style={{ marginTop: "36px", color: "#4a4a4a" }}>
                {hasCourseAccess
                  ? t("embarkOnWellnessJourneyPreventive")
                  : t("embarkOnWellnessJourney")}{" "}
              </p>
              <p style={{ color: "#4a4a4a" }}>
                {t("experiencePersonalizedPhysio")}
              </p>
              <p style={{ color: "#4a4a4a" }}>
                {hasCourseAccess
                  ? t("takeControlandTailorPreventive")
                  : t("takeControlandTailor")}{" "}
              </p>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  width: "100%",
                  marginTop: "24px",
                }}
              >
                <h1 style={{ color: "#063bd0", fontSize: "32px", margin: 0 }}>
                  {hasCourseAccess
                    ? "€119,99"
                    : currentProduct === "physio_user"
                      ? ""
                      : currentProduct === "physio_therapist"
                        ? ""
                        : "€6,99"}
                </h1>
                <Button
                  style={{
                    padding: "12px 24px",
                    marginLeft: "24px",
                    cursor: "pointer",
                    fontSize: "16px",
                    borderRadius: "4px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  }}
                  onClick={onBuy}
                  disabled={isLoading}
                >
                  {isLoading
                    ? t("loading")
                    : currentProduct === "physio_user"
                      ? t("tryForFree")
                      : currentProduct === "physio_therapist"
                        ? t("tryForFree")
                        : t("buy")}
                </Button>
              </div>
            </Box>
          </div>
        </Grid>
      </Grid>
      {/* Error dialog (opens for any checkout error) */}
      <Dialog open={errorDialogOpen} onClose={handleDialogClose}>
        <DialogTitle>{t("checkoutError")}</DialogTitle>
        <DialogContent>
          <Typography>
            {errorMessage === null ? t("checkoutErrorOccurred") : errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            onClick={handleDialogClose}
          >
            {t("close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Checkout;
